<script lang="ts" setup>
import { IconChevronRight, IconCircle, IconCircleCheckFilled, IconCircleDotFilled } from '@/icons';

interface Props {
  items: string[];
  activeIndex: number;
}

const { items = [], activeIndex = 0 } = defineProps<Props>();
</script>

<template>
  <div data-ui-component="UiStepper" :class="$style.root">
    <div v-for="(item, i) in items" :key="i" :class="$style.step">
      <div
        :class="[
          $style.stepContent,
          { [$style.completed]: i < activeIndex, [$style.active]: activeIndex === i },
        ]"
      >
        <div :class="$style.itemIcon">
          <IconCircleCheckFilled v-if="i < activeIndex" aria-hidden="true" color="#18a582" />
          <template v-else-if="i === activeIndex">
            <IconCircleDotFilled aria-hidden="true" color="black" />
          </template>
          <template v-else>
            <IconCircle aria-hidden="true" color="#D2D3D9" />
          </template>
        </div>
        <div :class="$style.stepLabel">
          {{ item }}
        </div>
      </div>
      <div v-if="i < items.length - 1" :class="$style.connector">
        <slot name="connector">
          <IconChevronRight aria-hidden="true" color="#D2D3D9" size="16" />
        </slot>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/colors';

.root {
  display: inline-flex;
}

.step {
  display: flex;
  position: relative;
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
  text-align: center;
  justify-content: center;
  user-select: none;
}

.stepLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
}

.stepContent {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  max-width: 260px;
  min-width: 80px;
  overflow: hidden;
  padding: 10px;
  border-radius: 10px;

  &:not(.active) {
    color: #978f91;
  }

  &.completed {
    color: #18a582;
  }
}

.connector {
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 4px;
}

.itemIcon {
  display: flex;
  flex-shrink: 0;
  position: relative;
  gap: 8px;
}
</style>
